import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
 FaBatteryFull,
 FaBoxOpen,
 FaChargingStation,
 FaCheckCircle,
 FaClipboardList,
 FaGripLines,
 FaHeadset,
 FaLayerGroup,
 FaMicrochip,
 FaPlug,
 FaShippingFast,
 FaTape,
 FaTools,
} from "react-icons/fa";
import { MdElectricScooter } from "react-icons/md";
import Hero from "../../components/Hero/Hero";
import CategoryGrid from "../../components/CategoryGrid/CategoryGrid";
import ProductGrid from "../../components/ProductGrid/ProductGrid";
import Pagination from "../../components/Pagination/Pagination";
import LoadingSkeleton from "../../components/LoadingSkeleton/LoadingSkeleton";
import SectionTitle from "../../components/ui/SectionTitle";
import Button from "../../components/ui/Button";
import FAQAccordion from "../../components/FAQAccordion/FAQAccordion";
import ScootyFeatureShowcase from "../../components/ScootyFeatureShowcase/ScootyFeatureShowcase";
import brands from "../../data/brands.json";
import industries from "../../data/industries.json";
import testimonials from "../../data/testimonials.json";
import faqs from "../../data/faqs.json";
import { getCategories } from "../../services/categoryService";
import { getFeaturedProducts } from "../../services/productService";
import { getCustomerFeedback } from "../../services/homeContentService";


const brandIcons = {
 battery: FaBatteryFull,
 scooter: MdElectricScooter,
 box: FaBoxOpen,
 cell: FaMicrochip,
 holder: FaClipboardList,
 charger: FaChargingStation,
 leadCharger: FaPlug,
 leadBattery: FaBatteryFull,
 tools: FaTools,
 nickel: FaGripLines,
 epoxy: FaLayerGroup,
 tape: FaTape,
};
const featuredPageSize = 12;

const why = [
 [
  "Verified Catalogue",
  "Curated products, brands, specifications and MOQ details for faster procurement.",
  FaCheckCircle,
 ],
 [
  "Bulk Quote Support",
  "Add multiple products and submit project-level requirements without checkout noise.",
  FaClipboardList,
 ],
 [
  "Responsive Sales Team",
  "Manual quotation support for GST, delivery, availability and bulk negotiation.",
  FaHeadset,
 ],
 [
  "Fast Dispatch Support",
  "Supply coordination for dealers, pack builders, workshops and service teams.",
  FaShippingFast,
 ],
];

const customerFeedback = [
 {
  name: "Rohit Verma",
  company: "EV Pack Solutions",
  image:
   "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=240&q=80",
  quote:
   "Mozo catalogue se cells aur BMS shortlist karna fast ho gaya. Quote team bhi quickly respond karti hai.",
 },
 {
  name: "Neha Patel",
  company: "Solar Storage Works",
  image:
   "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=240&q=80",
  quote:
   "Product details clear hain, MOQ aur stock dekh kar enquiry bhejna easy lagta hai.",
 },
 {
  name: "Imran Khan",
  company: "GreenRide Service Hub",
  image:
   "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=240&q=80",
  quote:
   "Chargers, connectors aur scooty parts ke options ek jagah mil jaate hain. Procurement simple ho gayi.",
 },
 {
  name: "Amit Sharma",
  company: "Metro EV Repairs",
  image:
   "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=240&q=80",
  quote:
   "Bulk requirement bhejne ke baad quotation follow-up clean aur professional tha.",
 },
];


const getBrandProductsPath = (brand) => {
 const categories = Array.isArray(brand.categories)
  ? brand.categories.filter(Boolean)
  : [];
 if (categories.length) {
  return `/products?categories=${encodeURIComponent(categories.join(","))}`;
 }
 return `/products?q=${encodeURIComponent(brand.query || brand.name)}`;
};

export default function Home() {
 const [categories, setCategories] = useState([]);
 const [featured, setFeatured] = useState([]);
 const [featuredPage, setFeaturedPage] = useState(1);
 const [feedback, setFeedback] = useState(customerFeedback);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
  Promise.all([getCategories(), getFeaturedProducts()])
   .then(([categoryData, productData]) => {
    setCategories(categoryData);
    setFeatured(productData);
   })
   .catch(() => {
    setCategories([]);
    setFeatured([]);
   })
   .finally(() => setLoading(false));
 }, []);

 useEffect(() => {
  getCustomerFeedback().then((feedbackData) => {
   if (feedbackData) setFeedback(feedbackData);
  });
 }, []);

 const featuredTotalPages = Math.max(
  1,
  Math.ceil(featured.length / featuredPageSize),
 );
 const visibleFeatured = featured.slice(
  (featuredPage - 1) * featuredPageSize,
  featuredPage * featuredPageSize,
 );

 return (
  <>
   <Hero />
   <section className="container-pad py-10 sm:py-14">
    <div className="flex gap-3 overflow-x-auto pb-3 sm:gap-4 lg:grid lg:grid-cols-6 lg:overflow-visible lg:pb-0">
     {brands.map((brand) => {
      const Icon = brandIcons[brand.icon] || FaBoxOpen;
      return (
       <Link
        key={brand.name}
        to={getBrandProductsPath(brand)}
        className="grid h-16 min-w-[240px] grid-cols-[82px_1fr] overflow-hidden rounded-xl border border-line bg-white text-left shadow-sm transition hover:-translate-y-1 hover:border-primary hover:shadow-soft focus:outline-none focus:ring-2 focus:ring-primary/30 sm:min-w-[250px] lg:min-w-0"
       >
        <span className="grid h-full place-items-center border-r border-line bg-emerald-50 text-3xl text-primary">
         <Icon aria-hidden="true" />
        </span>
        <span className="flex min-w-0 flex-col items-center justify-center px-2 py-1 text-center">
         <span className="block max-w-full text-[13px] font-extrabold leading-tight text-[#062B3A] sm:text-sm">
          {brand.name}
         </span>
         <span className="mt-0.5 block max-w-full text-[10px] font-extrabold uppercase tracking-wide text-[#42566B] sm:text-[11px]">
          {brand.tag}
         </span>
        </span>
       </Link>
      );
     })}
    </div>
   </section>


   <ScootyFeatureShowcase />
   <section className="container-pad py-12">
    <SectionTitle
     eyebrow="Browse categories"
     title="EV battery products organized for serious procurement teams"
     description="Explore cells, battery packs, BMS, chargers, machines, connectors, nickel strips, insulation and pack accessories."
    />
    {loading ? <LoadingSkeleton /> : <CategoryGrid categories={categories} />}
   </section>

   <section className="bg-white py-16">
    <div className="container-pad">
     <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
      <SectionTitle
       eyebrow="Featured products"
       title="High-demand EV battery products"
       description="No pricing, no checkout. Add required products to quote and share your requirement."
      />
      <Button to="/products" variant="secondary">
       View All Products
      </Button>
     </div>
     {loading ? (
      <LoadingSkeleton />
     ) : (
      <ProductGrid products={visibleFeatured} />
     )}
     <Pagination
      page={featuredPage}
      totalPages={featuredTotalPages}
      onChange={setFeaturedPage}
     />
    </div>
   </section>



   <section className="container-pad py-16">
    <SectionTitle
     eyebrow="Why choose us"
     title="A cleaner procurement experience for EV product buyers"
     align="center"
    />
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
     {why.map(([title, description, Icon]) => (
      <div
       key={title}
       className="rounded-xl border border-line bg-white p-6 shadow-soft transition hover:-translate-y-1 hover:shadow-lift"
      >
       <span className="grid h-12 w-12 place-items-center rounded-xl bg-emerald-50 text-xl text-primary">
        <Icon />
       </span>
       <h3 className="mt-5 text-lg font-semibold leading-snug text-dark">
        {title}
       </h3>
       <p className="mt-3 text-base leading-7 text-body">{description}</p>
      </div>
     ))}
    </div>
   </section>

   <section className="bg-white py-16">
    <div className="container-pad overflow-hidden">
     <SectionTitle
      eyebrow="Happy customer feedback"
      title="Real buyers sharing their Mozo experience"
      // description="Customer photos and comments slide continuously so new visitors can quickly see buyer confidence."
      align="center"
     />
     <div className="feedback-marquee mt-8">
      <div className="feedback-track">
       {[...feedback, ...feedback].map((item, index) => (
        <figure key={`${item.name}-${index}`} className="feedback-card">
         <img src={item.image} alt={item.name} />
         <blockquote>"{item.quote}"</blockquote>
         <figcaption>
          <strong>{item.name}</strong>
          <span>{item.company}</span>
         </figcaption>
        </figure>
       ))}
      </div>
     </div>
    </div>
   </section>

   <section className="bg-white py-16">
    <div className="container-pad">
     <SectionTitle
      eyebrow="Industries"
      title="Industries we serve"
      description="Built for teams who need product clarity, reliable sourcing and quick quote handoffs."
     />
     <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
      {industries.map((industry) => (
       <div
        key={industry.name}
        className="rounded-xl border border-line bg-base p-6"
       >
        <h3 className="text-lg font-semibold text-dark">{industry.name}</h3>
        <p className="mt-3 text-base leading-7 text-body">
         {industry.description}
        </p>
       </div>
      ))}
     </div>
    </div>
   </section>

   <section className="container-pad grid gap-12 py-16 lg:grid-cols-2 lg:items-start">
    <div>
     <SectionTitle
      eyebrow="How it works"
      title="From product discovery to manual quotation"
     />
     <div className="grid gap-4">
      {[
       "Browse EV categories and products",
       "Add required parts to quote cart",
       "Share company, GST, voltage, Ah and quantity details",
       "Receive a manual quotation from the sales team",
      ].map((step, index) => (
       <div
        key={step}
        className="flex gap-4 rounded-xl border border-line bg-white p-5 shadow-sm"
       >
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary font-bold text-white">
         {index + 1}
        </span>
        <p className="font-bold text-dark">{step}</p>
       </div>
      ))}
     </div>
    </div>
    <div>
     <SectionTitle eyebrow="FAQs" title="Common procurement questions" />
     <FAQAccordion faqs={faqs} />
    </div>
   </section>

   <section className="bg-dark py-16 text-white">
    <div className="container-pad grid gap-8 lg:grid-cols-[1fr_420px] lg:items-center">
     <div>
      <p className="text-sm font-bold uppercase tracking-wider text-primary">
       Contact CTA
      </p>
      <h2 className="mt-3 text-2xl font-bold leading-snug sm:text-3xl">
       Planning a bulk EV battery requirement?
      </h2>
      <p className="mt-4 max-w-3xl leading-7 text-slate-300">
       Send your product list, cell grade, BMS configuration, charger
       requirement or pack notes. Our team will coordinate the best quotation
       manually.
      </p>
     </div>
     <Button to="/request-quote" className="w-full">
      Request Quote
     </Button>
    </div>
   </section>

   <section className="container-pad py-16">
    <SectionTitle
     eyebrow="Testimonials"
     title="Trusted by project procurement teams"
     align="center"
    />
    <div className="grid gap-6 md:grid-cols-3">
     {testimonials.map((item) => (
      <figure
       key={item.name}
       className="rounded-xl border border-line bg-white p-6 shadow-soft"
      >
       <blockquote className="leading-7 text-body">"{item.quote}"</blockquote>
       <figcaption className="mt-5 font-semibold text-dark">
        {item.name}
        <span className="block text-sm font-semibold text-slate-500">
         {item.company}
        </span>
       </figcaption>
      </figure>
     ))}
    </div>
   </section>
  </>
 );
}


